import React, { useState, useEffect, useCallback, useRef } from 'react';
import { devError } from '../lib/devLog';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import PriceTag from '../components/PriceTag';
import { Watermark } from '../components/ContentProtection';
import OptimisedImage from '../components/OptimisedImage';
import Footer from '../components/Footer';
import ViewOnWall from '../components/ViewOnWall';
import PriceRequestModal from '../components/PriceRequestModal';
import BuyArtworkModal from '../components/BuyArtworkModal';
import NotFoundPage from './NotFoundPage';
import SEO from '../components/SEO';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Heart, Share2, Eye, Video, ChevronLeft, ChevronRight, Facebook, Twitter, Mail, Link as LinkIcon, ZoomIn, MessageCircle, ShoppingBag } from 'lucide-react';
import RelatedArtworks from '../components/artwork/RelatedArtworks';
import ArtworkSpecs from '../components/artwork/ArtworkSpecs';
import ArtworkAbout from '../components/artwork/ArtworkAbout';
import { addsUkVat } from '../lib/vat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Break a long description into readable paragraphs.
 *
 * Strategy:
 *  1. If the text already contains real paragraph breaks (\n\n), respect them.
 *  2. Otherwise, split into sentences and group ~3 sentences per paragraph.
 *     Aim for paragraphs of ~280–360 characters — readable without scrolling
 *     past walls of text on either mobile or desktop.
 */
const formatIntoParagraphs = (raw) => {
  if (!raw) return [];
  const text = String(raw).trim();
  if (!text) return [];

  // 1) Respect authored paragraph breaks
  if (/\n\s*\n/.test(text)) {
    return text
      .split(/\n\s*\n/)
      .map((p) => p.replace(/\s+/g, ' ').trim())
      .filter(Boolean);
  }

  // 2) Split into sentences (.!?) and group
  const sentences = text
    .replace(/\s+/g, ' ')
    .match(/[^.!?]+[.!?]+(?:["'”’)]+)?|\S[^.!?]*$/g);

  if (!sentences || sentences.length <= 3) return [text];

  const paragraphs = [];
  let buffer = '';
  const TARGET_MIN = 240;
  for (let i = 0; i < sentences.length; i += 1) {
    const s = sentences[i].trim();
    buffer = buffer ? `${buffer} ${s}` : s;
    const isLast = i === sentences.length - 1;
    if (buffer.length >= TARGET_MIN || isLast) {
      paragraphs.push(buffer);
      buffer = '';
    }
  }
  return paragraphs;
};

const ArtworkDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [artwork, setArtwork] = useState(null);
  const [artist, setArtist] = useState(null);
  const [relatedArtworks, setRelatedArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFoundKind, setNotFoundKind] = useState(null); // 'gone' | 'missing' | null
  const [showViewOnWall, setShowViewOnWall] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [saved, setSaved] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [imageZoomed, setImageZoomed] = useState(false);
  const [prevNextIds, setPrevNextIds] = useState({ prev: null, next: null });

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchArtwork();
    
    // Track recently viewed
    const viewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
    if (!viewed.includes(id)) {
      viewed.unshift(id);
      localStorage.setItem('recentlyViewed', JSON.stringify(viewed.slice(0, 10)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchArtwork = async () => {
    try {
      const response = await fetch(`${API_URL}/api/artworks/${id}`);
      if (!response.ok) {
        // Check if this artwork was explicitly deleted (410 Gone signal)
        try {
          const tomb = await fetch(`${API_URL}/api/deleted/artwork/${id}`);
          if (tomb.ok) {
            const td = await tomb.json();
            if (td.deleted) {
              setNotFoundKind('gone');
              setLoading(false);
              return;
            }
          }
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') console.debug('[artwork] alias lookup failed:', err);
        }
        setNotFoundKind('missing');
        setLoading(false);
        return;
      }
      const data = await response.json();
      setArtwork(data);

      // Fetch artist data
      if (data.artistId) {
        try {
          const artistRes = await fetch(`${API_URL}/api/artists/${data.artistId}`);
          if (artistRes.ok) {
            const artistData = await artistRes.json();
            setArtist(artistData);
          }
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('Artist data not available');
          }
        }
      }

      // Fetch related artworks by same artist
      // Limit=200 so the same-artist filter actually finds neighbours for
      // any of the gallery's ~1100 works (50 was too narrow — e.g. Banksy
      // pieces alphabetically late in the list got an empty carousel).
      const relatedRes = await fetch(`${API_URL}/api/artworks?limit=200`);
      const allArtworks = await relatedRes.json();
      // The URL param `id` may be either the artwork's UUID or its slug.
      // Match on either so prev/next/related work regardless of which form
      // the user landed on.
      const related = allArtworks
        .filter(a => a.id !== data.id && a.artistName === data.artistName)
        .slice(0, 12);
      setRelatedArtworks(related);

      // Get prev/next artworks — key by slug when available, id otherwise,
      // so navigation uses the canonical SEO-friendly URL.
      const currentIndex = allArtworks.findIndex(a => a.id === data.id);
      const keyOf = (a) => a.slug || a.id;
      setPrevNextIds({
        prev: currentIndex > 0 ? keyOf(allArtworks[currentIndex - 1]) : null,
        next: currentIndex < allArtworks.length - 1 ? keyOf(allArtworks[currentIndex + 1]) : null
      });

      // Canonicalise the URL: if the user arrived by UUID but the artwork
      // has a slug, quietly rewrite the browser URL to the slug form so any
      // shares / bookmarks / analytics use the SEO-friendly path.
      if (data.slug && id !== data.slug && typeof window !== 'undefined') {
        window.history.replaceState({}, '', `/artwork/${data.slug}${window.location.search}${window.location.hash}`);
      }

      setLoading(false);
    } catch (error) {
      devError('Error fetching artwork:', error);
      setNotFoundKind('missing');
      setLoading(false);
    }
  };

  const shareToSocial = (platform) => {
    // Use backend /api/share/artwork/:id endpoint as the share target so
    // WhatsApp / iMessage / Twitter / Facebook / LinkedIn scrape rich OG previews.
    // Real users hitting that URL get auto-redirected back to the SPA.
    const shareUrl = `${window.location.origin}/api/share/artwork/${artwork.id}`;
    const title = `${artwork.title} by ${artwork.artistName}`;

    const shareUrls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${title} — ${shareUrl}`)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(title)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Check out this artwork: ${shareUrl}`)}`
    };

    if (platform === 'copy') {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    } else if (platform === 'whatsapp') {
      // WhatsApp opens in same window on mobile (PWA-friendly), new tab on desktop
      window.open(shareUrls.whatsapp, '_blank', 'noopener,noreferrer');
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (notFoundKind) {
    return <NotFoundPage deleted={notFoundKind === 'gone'} />;
  }

  if (!artwork) return null;

  const imageUrl = artwork.imageUrl?.startsWith('http') 
    ? artwork.imageUrl 
    : `${API_URL}${artwork.imageUrl}`;

  // Render the description authored in the CMS / generated by the AI tools.
  // Priority: longDescription (long-form CMS field) → description (default)
  // Only fall back to generated boilerplate if BOTH are missing or empty.
  const hasAuthoredDescription =
    (artwork.longDescription && artwork.longDescription.trim().length > 30) ||
    (artwork.description && artwork.description.trim().length > 30);

  const aboutTheWork = hasAuthoredDescription
    ? (artwork.longDescription?.trim() || artwork.description?.trim())
    : `${artwork.title} by ${artwork.artistName} is a ${artwork.medium?.toLowerCase() || 'contemporary artwork'}${artwork.year ? `, created in ${artwork.year}` : ''}. ${artwork.dimensions ? `Measuring ${artwork.dimensions.toLowerCase()}, ` : ''}this work is part of Zebra One Gallery's carefully curated collection. Condition reports and provenance available upon request.`;

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title={(() => {
          // Keep SEO titles under 60 chars for Google truncation.
          // Priority: artist-first (buyers search by artist), then artwork title, truncated.
          const brand = 'Zebra One';
          const artist = artwork.artistName || '';
          const t = artwork.title || '';
          // "Artist — Title | Zebra One" — target ≤60
          let candidate = `${artist} — ${t} | ${brand}`;
          if (candidate.length <= 60) return candidate;
          // Truncate title
          const overhead = artist.length + 3 + 3 + brand.length; // " — " + " | "
          const room = Math.max(20, 60 - overhead);
          const truncated = t.length > room ? `${t.slice(0, room - 1).trimEnd()}…` : t;
          return `${artist} — ${truncated} | ${brand}`;
        })()}
        description={artwork.seoDescription || `${artwork.title} (${artwork.year || 'n.d.'}) by ${artwork.artistName}. ${artwork.medium || 'Artwork'}${artwork.dimensions ? `, ${artwork.dimensions}` : ''}. ${artwork.available ? 'Available at Zebra One Gallery, Hampstead London.' : 'Sold. Archive reference.'} Price on request.`}
        image={imageUrl}
        canonical={typeof window !== 'undefined' ? window.location.href.split('?')[0] : undefined}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'VisualArtwork',
          name: artwork.title,
          image: imageUrl,
          creator: {
            '@type': 'Person',
            name: artwork.artistName,
          },
          artform: artwork.medium || 'Contemporary art',
          artMedium: artwork.medium,
          dateCreated: artwork.year,
          ...(artwork.dimensions && { size: artwork.dimensions }),
          ...(artwork.edition && { artEdition: artwork.edition }),
          offers: {
            '@type': 'Offer',
            availability: artwork.available
              ? 'https://schema.org/InStock'
              : 'https://schema.org/SoldOut',
            priceSpecification: {
              '@type': 'PriceSpecification',
              priceCurrency: 'GBP',
              price: 'Price on request',
            },
            seller: {
              '@type': 'ArtGallery',
              name: 'Zebra One Gallery',
              address: {
                '@type': 'PostalAddress',
                addressLocality: 'Hampstead',
                addressRegion: 'London',
                addressCountry: 'GB',
              },
            },
          },
        }}
      />
      <Header />
      
      <div className="pt-20">
        {/* Navigation Bar */}
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-12 py-4 border-b border-neutral-200">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/shop')}
              className="flex items-center gap-2 text-sm text-neutral-600 hover:text-black transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Gallery
            </button>

            <div className="flex items-center gap-2">
              {prevNextIds.prev && (
                <Link
                  to={`/artwork/${prevNextIds.prev}`}
                  className="p-2 border border-neutral-300 hover:border-black rounded transition-colors"
                  title="Previous artwork"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Link>
              )}
              {prevNextIds.next && (
                <Link
                  to={`/artwork/${prevNextIds.next}`}
                  className="p-2 border border-neutral-300 hover:border-black rounded transition-colors"
                  title="Next artwork"
                >
                  <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-12 py-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            
            {/* LEFT: Image */}
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="relative bg-neutral-50 flex items-center justify-center p-8 cursor-zoom-in"
                style={{ minHeight: '600px' }}
                onClick={() => setImageZoomed(!imageZoomed)}
              >
                {imageUrl ? (
                  <OptimisedImage
                    src={artwork.imageUrl}
                    alt={artwork.title}
                    width={1200}
                    loading="eager"
                    fetchPriority="high"
                    className={`max-w-full max-h-[700px] object-contain transition-transform duration-300 ${
                      imageZoomed ? 'scale-150' : 'scale-100'
                    }`}
                  />
                ) : (
                  <div className="text-neutral-400 text-sm">Image not available</div>
                )}
                {imageUrl && <Watermark />}
                {!imageZoomed && (
                  <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur text-white px-3 py-2 rounded-full text-xs flex items-center gap-2">
                    <ZoomIn className="w-3 h-3" />
                    Click to zoom
                  </div>
                )}
              </motion.div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSaved(!saved)}
                  className={`flex items-center gap-2 px-4 py-2 border rounded-full text-sm transition-all ${
                    saved 
                      ? 'border-black bg-black text-white' 
                      : 'border-neutral-300 hover:border-black'
                  }`}
                >
                  <Heart className={`w-4 h-4 ${saved ? 'fill-current' : ''}`} />
                  {saved ? 'Saved' : 'Save'}
                </button>

                <div className="relative">
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    className="flex items-center gap-2 px-4 py-2 border border-neutral-300 hover:border-black rounded-full text-sm transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>

                  <AnimatePresence>
                    {showShareMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full mt-2 left-0 bg-white border border-neutral-200 rounded-lg shadow-xl p-2 w-48 z-10"
                      >
                        <button
                          onClick={() => shareToSocial('whatsapp')}
                          data-testid="share-whatsapp-btn"
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-neutral-50 rounded text-sm"
                        >
                          <MessageCircle className="w-4 h-4 text-[#25D366]" />
                          WhatsApp
                        </button>
                        <button onClick={() => shareToSocial('facebook')} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-neutral-50 rounded text-sm">
                          <Facebook className="w-4 h-4" />
                          Facebook
                        </button>
                        <button onClick={() => shareToSocial('twitter')} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-neutral-50 rounded text-sm">
                          <Twitter className="w-4 h-4" />
                          Twitter
                        </button>
                        <button onClick={() => shareToSocial('pinterest')} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-neutral-50 rounded text-sm">
                          <Share2 className="w-4 h-4" />
                          Pinterest
                        </button>
                        <button onClick={() => shareToSocial('email')} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-neutral-50 rounded text-sm">
                          <Mail className="w-4 h-4" />
                          Email
                        </button>
                        <hr className="my-2" />
                        <button onClick={() => shareToSocial('copy')} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-neutral-50 rounded text-sm">
                          <LinkIcon className="w-4 h-4" />
                          Copy link
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>

            {/* RIGHT: Details */}
            <div className="space-y-8 lg:pt-0">
              
              {/* Artist */}
              <div>
                <button
                  onClick={() => navigate(`/shop?artist=${encodeURIComponent(artwork.artistName)}`)}
                  className="text-lg text-neutral-600 hover:text-black transition-colors underline-offset-4 hover:underline"
                >
                  {artwork.artistName}
                </button>
              </div>

              {/* Title & Year */}
              <div className="space-y-2">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light leading-tight">
                  {artwork.title}
                </h1>
                {artwork.year && (
                  <p className="text-xl text-neutral-500">{artwork.year}</p>
                )}
              </div>

              {/* Specifications */}
              <ArtworkSpecs
                artwork={artwork}
                onFormatClick={(f) => {
                  setSelectedFormat(f);
                  // Multi-size works with a listed price go straight to Stripe
                  // Checkout for that tier; enquiry-only artists (Banksy) or
                  // works without a numeric price fall back to Enquire.
                  const canCheckout =
                    typeof f.price === 'number'
                    && f.price > 0
                    && f.available !== false
                    && (artwork.artistName || '').toLowerCase() !== 'banksy';
                  if (canCheckout) {
                    setShowBuyModal(true);
                  } else {
                    setShowPriceModal(true);
                  }
                }}
              />

              {/* Price & CTAs */}
              <div className="space-y-4">
                <PriceTag
                  priceDisplay={artwork.priceDisplay || 'Price on Request'}
                  vatScheme={artwork.vatScheme || 'standard'}
                  variant="full"
                />
                {addsUkVat(artwork) && Number(artwork.price) > 0 && (
                  <p className="text-xs text-neutral-500 -mt-2" data-testid="artwork-vat-note">
                    UK VAT and shipping added at checkout
                  </p>
                )}
                {artwork.vatScheme === 'margin' && Number(artwork.price) > 0 && (
                  <p className="text-xs text-neutral-500 -mt-2" data-testid="artwork-margin-note">
                    VAT Margin Scheme — no VAT added · shipping added at checkout
                  </p>
                )}

                {artwork.available && (
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Buy now — only when the work has a real numeric price
                        and isn't enquiry-only (Banksy etc.). The backend
                        re-validates both conditions so this is presentation
                        only, never the source of truth. */}
                    {typeof artwork.price === 'number' && artwork.price > 0
                      && (artwork.artistName || '').toLowerCase() !== 'banksy' && (
                      <button
                        onClick={() => setShowBuyModal(true)}
                        className="inline-flex items-center gap-2 h-11 px-6 bg-black text-white hover:bg-neutral-800 transition-colors text-xs uppercase tracking-wider font-medium"
                        data-testid="artwork-buy-btn"
                      >
                        <ShoppingBag className="w-4 h-4" />
                        Buy now
                      </button>
                    )}

                    <button
                      onClick={() => setShowPriceModal(true)}
                      className={`px-8 py-3 ${
                        typeof artwork.price === 'number' && artwork.price > 0
                          && (artwork.artistName || '').toLowerCase() !== 'banksy'
                          ? 'border border-black text-black hover:bg-black hover:text-white'
                          : 'bg-black text-white hover:bg-neutral-800'
                      } transition-colors text-xs uppercase tracking-wider font-medium`}
                      data-testid="artwork-request-price-btn"
                    >
                      {artwork.priceDisplay ? 'Enquire About This Work' : 'Request Price'}
                    </button>

                    {artwork.category !== 'Sculpture' && (
                      <button
                        onClick={() => setShowViewOnWall(true)}
                        aria-label="View on your wall"
                        title="View on your wall"
                        data-testid="artwork-view-on-wall-btn"
                        className="group inline-flex items-center h-11 px-4 sm:px-3 sm:hover:px-5 border border-black text-black hover:bg-black hover:text-white transition-all duration-300 text-xs uppercase tracking-wider font-medium overflow-hidden"
                      >
                        <Eye className="w-4 h-4 shrink-0 sm:transition-transform" />
                        {/* Mobile always shows the label; desktop slides it out on hover */}
                        <span className="ml-2 sm:ml-0 sm:max-w-0 sm:opacity-0 sm:group-hover:ml-2 sm:group-hover:max-w-[160px] sm:group-hover:opacity-100 whitespace-nowrap transition-all duration-300">
                          View on your wall
                        </span>
                      </button>
                    )}

                    {artwork.category === 'Sculpture' && (
                      <button
                        onClick={() => setVideoModalOpen(true)}
                        aria-label="Request a video"
                        title="Request a video"
                        data-testid="artwork-request-video-btn"
                        className="group inline-flex items-center h-11 px-4 sm:px-3 sm:hover:px-5 border border-black text-black hover:bg-black hover:text-white transition-all duration-300 text-xs uppercase tracking-wider font-medium overflow-hidden"
                      >
                        <Video className="w-4 h-4 shrink-0" />
                        <span className="ml-2 sm:ml-0 sm:max-w-0 sm:opacity-0 sm:group-hover:ml-2 sm:group-hover:max-w-[160px] sm:group-hover:opacity-100 whitespace-nowrap transition-all duration-300">
                          Request a video
                        </span>
                      </button>
                    )}

                    {/* Send to a friend — prominent share CTA */}
                    <button
                      onClick={() => setShowShareMenu(true)}
                      aria-label="Send this artwork to a friend"
                      title="Send to a friend"
                      data-testid="artwork-send-to-friend-btn"
                      className="group inline-flex items-center h-11 px-4 sm:px-3 sm:hover:px-5 border border-black text-black hover:bg-black hover:text-white transition-all duration-300 text-xs uppercase tracking-wider font-medium overflow-hidden"
                    >
                      <Share2 className="w-4 h-4 shrink-0" />
                      <span className="ml-2 sm:ml-0 sm:max-w-0 sm:opacity-0 sm:group-hover:ml-2 sm:group-hover:max-w-[160px] sm:group-hover:opacity-100 whitespace-nowrap transition-all duration-300">
                        Send to a friend
                      </span>
                    </button>

                    <p className="w-full text-xs text-neutral-500 pt-1">
                      Condition reports and provenance available upon request
                    </p>
                  </div>
                )}

                {!artwork.available && (
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={() => setShowShareMenu(true)}
                      data-testid="artwork-send-to-friend-btn-sold"
                      className="inline-flex items-center gap-2 h-11 px-4 border border-black text-black hover:bg-black hover:text-white transition-all duration-300 text-xs uppercase tracking-wider font-medium"
                    >
                      <Share2 className="w-4 h-4" />
                      Send to a friend
                    </button>
                  </div>
                )}
              </div>

              <ArtworkAbout
                aboutTheWork={aboutTheWork}
                artist={artist}
                formatIntoParagraphs={formatIntoParagraphs}
              />

            </div>
          </div>

          {/* More by Artist Section - Carousel */}
          <RelatedArtworks
            artworks={relatedArtworks}
            artistName={artwork.artistName}
            viewAllHref={`/shop?artist=${encodeURIComponent(artwork.artistName)}`}
          />
        </div>
      </div>

      <Footer />
      
      {/* View on Wall Modal */}
      {artwork && (
        <ViewOnWall
          artwork={artwork}
          isOpen={showViewOnWall}
          onClose={() => setShowViewOnWall(false)}
        />
      )}

      {/* Price Request Modal */}
      <PriceRequestModal
        open={showPriceModal}
        onClose={() => { setShowPriceModal(false); setSelectedFormat(null); }}
        artwork={artwork}
        selectedFormat={selectedFormat}
      />

      {/* Video Request Modal (sculpture only) */}
      <PriceRequestModal
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        artwork={artwork}
        intent="video"
      />

      {/* Buy now — Stripe Checkout (non-Banksy, priced works only) */}
      <BuyArtworkModal
        open={showBuyModal}
        onClose={() => { setShowBuyModal(false); setSelectedFormat(null); }}
        artwork={artwork}
        selectedFormat={selectedFormat}
      />
    </div>
  );
};

export default ArtworkDetailPage;
